import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useImmer } from 'use-immer';
import { endOfDay } from 'date-fns';
import { CourseEnrollmentComponent } from './course-enrollment-component';
import {
  CourseDto,
  CourseEnrollmentActivityDto,
  CourseEnrollmentDto,
  CreateCourseEnrollmentDto,
  LessonOccupancyDto,
  LessonStateDto,
  ReportType,
} from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { WithCitizenContext } from '../../../citizen/citizen-context';
import { DatePickerValue } from '../../../../components/date-picker';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { useMetaTitle, useQuery } from '../../../../hooks';
import { CancelCourseEnrollmentDialog } from '../components/cancel-course-enrollment-dialog';
import { ExistingLessonsDialog } from '../components/existing-lessons-dialog';
import { CourseCapacityWarningDialog } from '../components/course-capacity-warning-dialog';
import { NoLessonsInPeriodDialog } from '../components/no-lessons-in-period-dialog';
import { REFRESH_CITIZEN_SCHEDULE } from '../../utils';
import { useFeatureToggles } from '../../../../feature-toggles';

const attendanceClient = moduleClientFactory.createAttendanceClient(true);
const offerAndActivityClient = moduleClientFactory.createOfferAndActivityClient(true);
export const REFRESH_COURSE_ENROLLMENT_TABLE = 'REFRESH_COURSE_ENROLLMENT_TABLE';

export function CourseEnrollment() {
  const { citizenId, courseEnrollmentId } = useParams();
  const activityIdQuery = useQuery('activityId');
  const { isFeatureEnabled } = useFeatureToggles();
  const selectsCourseByActivity = isFeatureEnabled('EnableCourseSelectionByActivity');

  const [isLoading, setIsLoading] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isExistingLessonsDialogOpen, setIsExistingLessonsDialogOpen] = useState(false);
  const [lessonsWithoutFreeSeats, setLessonsWithoutFreeSeats] = useState<LessonOccupancyDto[]>([]);
  const [periodWithoutLessons, setPeriodWithoutLessons] = useState<{ beginAt: Date | string; endAt: Date | string }>();
  const [courseEnrollment, setCourseEnrollment] = useImmer<CourseEnrollment>({
    isActive: true,
    citizenId,
    isDeleted: false,
    desiredReportType: ReportType.Never,
    activityId: activityIdQuery,
    followDfdgActivityPeriod: !!activityIdQuery,
  });

  useMetaTitle(courseEnrollmentId ? 'Redigér fremmødeforløb' : 'Opret fremmødeforløb');

  const [activities, setActivities] = useState<CourseEnrollmentActivityDto[]>([]);
  const [errors, setErrors] = useState<CourseEnrollmentErrors>({});
  const [offerId, setOfferId] = useState<string>();
  const [hasStartedLessons, setHasStartedLessons] = useState(false);
  const [hasFutureLessons, setHasFutureLessons] = useState(false);

  const postUpdate = usePostToTab(REFRESH_COURSE_ENROLLMENT_TABLE);
  const postScheduleUpdate = usePostToTab(REFRESH_CITIZEN_SCHEDULE);

  function updateTableAndSchedule() {
    postUpdate(window.opener);
    postScheduleUpdate(window.opener);
  }

  useEffect(() => {
    loadData();
  }, []);

  async function loadContext(contextOfferId: string | undefined, enrolledActivityId: string | undefined) {
    const context = await attendanceClient.getCourseEnrollmentContext({ citizenId: citizenId!, offerId: contextOfferId, enrolledActivityId });
    setActivities(context.activities ?? []);
    return context;
  }

  async function loadData() {
    try {
      setIsLoading(true);

      let loadedEnrollment: CourseEnrollmentDto | undefined;
      let resolvedOfferId: string | undefined;

      if (courseEnrollmentId) {
        const courseEnrollmentResult = await attendanceClient.getCourseEnrollmentById({ courseEnrollmentId });
        if (courseEnrollmentResult.courseEnrollment) {
          loadedEnrollment = courseEnrollmentResult.courseEnrollment;
          setCourseEnrollment({ ...courseEnrollmentResult.courseEnrollment });

          // The course is read only when editing, so onCourseChange never fires and the offer has to be resolved here.
          const { course } = await attendanceClient.getCourseById({ courseId: loadedEnrollment.courseId });
          resolvedOfferId = course?.offerId;
          setOfferId(resolvedOfferId);
        }
        await loadLessonProgress();
      }

      if (activityIdQuery && !courseEnrollmentId) {
        const [activityResult, coursesResponse] = await Promise.all([
          offerAndActivityClient.getActivityDetailsById({ activityId: activityIdQuery }),
          attendanceClient.getActiveCourses(selectsCourseByActivity ? { activityId: activityIdQuery } : {}),
        ]);
        const activityOfferId = activityResult.activity?.offerId;
        const course = selectsCourseByActivity && !activityOfferId ? undefined : coursesResponse.courses?.find(x => x.offerId === activityOfferId);
        resolvedOfferId = activityOfferId;
        setOfferId(resolvedOfferId);
        applyCourse(course);
        setCourseEnrollment(draft => {
          draft.beginAt = activityResult.activity?.startDate;
          draft.endAt = activityResult.activity?.endDate;
          draft.caseId = activityResult.activity?.case?.caseId;
        });
      }

      const context = await loadContext(resolvedOfferId, loadedEnrollment?.activityId ?? activityIdQuery);
      if (context.isSubjectToWorkDuty && !courseEnrollmentId) {
        setCourseEnrollment(draft => {
          draft.hasDutyToWork = true;
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function getEffectivePeriod() {
    const { beginAt, endAt, activityId, followDfdgActivityPeriod } = courseEnrollment;
    const activity = activities.find(x => x.activityId === activityId);
    return {
      effectiveBeginAt: followDfdgActivityPeriod ? activity?.startDate : beginAt,
      effectiveEndAt: followDfdgActivityPeriod ? activity?.endDate : endAt,
    };
  }

  async function handleSaveCourseEnrollmentClick() {
    try {
      setIsLoading(true);
      const { courseId } = courseEnrollment;
      const { effectiveBeginAt, effectiveEndAt } = getEffectivePeriod();
      if (!courseId || !effectiveBeginAt || !effectiveEndAt) {
        return;
      }

      if (!courseEnrollmentId && (await hasLessonsOnCourseInPeriod(courseId, effectiveBeginAt, effectiveEndAt))) {
        setIsExistingLessonsDialogOpen(true);
        return;
      }

      if (!courseEnrollmentId && isFeatureEnabled('EnableAlertDialogOnNoLessons') && !(await courseHasLessonsInPeriod(courseId, effectiveBeginAt, effectiveEndAt))) {
        setPeriodWithoutLessons({ beginAt: effectiveBeginAt, endAt: effectiveEndAt });
        return;
      }

      const { lessons = [] } = await attendanceClient.getLessonsWithoutFreeSeats({
        courseId,
        beginAt: effectiveBeginAt,
        endAt: effectiveEndAt,
        excludedCourseEnrollmentId: courseEnrollmentId,
      });
      if (lessons.length > 0) {
        setLessonsWithoutFreeSeats(lessons);
        return;
      }

      await saveCourseEnrollment();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirmSave() {
    try {
      setIsLoading(true);
      await saveCourseEnrollment();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function saveCourseEnrollment() {
    const {
      courseId,
      caseId,
      createAdvisIfNotOnTime,
      activityId,
      createFollowUpAdvisOnMissingAttendance,
      createNegativeEventOnMissingAttendance,
      followDfdgActivityPeriod,
      hasDutyToWork,
      desiredReportType,
      isActive,
    } = courseEnrollment;
    const { effectiveBeginAt, effectiveEndAt } = getEffectivePeriod();

    if (courseId && caseId && effectiveBeginAt && effectiveEndAt && desiredReportType) {
      if (!courseEnrollmentId) {
        const createDto: CreateCourseEnrollmentDto = {
          citizenId,
          caseId,
          beginAt: effectiveBeginAt,
          courseId,
          createAdvisIfNotOnTime: createAdvisIfNotOnTime || false,
          createFollowUpAdvisOnMissingAttendance: createFollowUpAdvisOnMissingAttendance || false,
          createNegativeEventOnMissingAttendance: createNegativeEventOnMissingAttendance || false,
          desiredReportType,
          endAt: effectiveEndAt,
          followDfdgActivityPeriod: followDfdgActivityPeriod || false,
          hasDutyToWork: hasDutyToWork || false,
          isActive: true,
          isDeleted: false,
          activityId,
        };

        await attendanceClient.createCourseEnrollment({ courseEnrollment: createDto });
      } else {
        const updateDto: CourseEnrollmentDto = {
          courseEnrollmentId,
          citizenId,
          caseId,
          beginAt: effectiveBeginAt,
          courseId,
          createAdvisIfNotOnTime: createAdvisIfNotOnTime || false,
          createFollowUpAdvisOnMissingAttendance: createFollowUpAdvisOnMissingAttendance || false,
          createNegativeEventOnMissingAttendance: createNegativeEventOnMissingAttendance || false,
          desiredReportType,
          endAt: effectiveEndAt,
          followDfdgActivityPeriod: followDfdgActivityPeriod || false,
          hasDutyToWork: hasDutyToWork || false,
          isActive,
          isDeleted: false,
          activityId,
        };
        await attendanceClient.updateCourseEnrollment({ courseEnrollment: updateDto });
      }

      updateTableAndSchedule();
      window.close();
    }
  }

  async function hasLessonsOnCourseInPeriod(courseId: string, beginAt: Date | string, endAt: Date | string) {
    const { lessons } = await attendanceClient.getCourseEnrollmentLessonsByCitizenId({ citizenId: citizenId! });
    const periodBegin = new Date(beginAt);
    const periodEnd = new Date(endAt);

    return (lessons ?? []).some(
      lesson => lesson.courseId === courseId && lesson.lessonState !== LessonStateDto.Cancelled && new Date(lesson.beginAt) >= periodBegin && new Date(lesson.endAt) <= periodEnd
    );
  }

  async function courseHasLessonsInPeriod(courseId: string, beginAt: Date | string, endAt: Date | string) {
    const { lessons } = await attendanceClient.getLessonsByCourseId({ courseId });
    const periodBegin = new Date(beginAt);
    const periodEnd = endOfDay(new Date(endAt));

    return (lessons ?? []).some(lesson => new Date(lesson.beginAt) >= periodBegin && new Date(lesson.endAt) <= periodEnd);
  }

  async function loadLessonProgress() {
    try {
      const { lessons } = await attendanceClient.getCourseEnrollmentLessonsByCitizenId({ citizenId });
      const now = new Date();
      const own = (lessons ?? []).filter(lesson => lesson.courseEnrollmentId === courseEnrollmentId);

      setHasStartedLessons(own.some(lesson => new Date(lesson.beginAt) <= now));
      setHasFutureLessons(own.some(lesson => new Date(lesson.beginAt) > now));
    } catch (error) {
      console.error(error);
    }
  }

  function handleCancelCourseEnrollmentClick() {
    setIsCancelDialogOpen(true);
  }

  async function handleConfirmCancelCourseEnrollment() {
    try {
      setIsLoading(true);
      await attendanceClient.cancelCourseEnrollment({ courseEnrollmentId: courseEnrollmentId! });
      updateTableAndSchedule();
      window.close();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleValidate() {
    const tmpErrors = {} as CourseEnrollmentErrors;
    let isValid = true;

    const { courseId, beginAt, endAt, desiredReportType, followDfdgActivityPeriod, activityId } = courseEnrollment;

    if (!courseId) {
      tmpErrors.courseId = 'Hold skal vælges';
      isValid = false;
    }

    if (!courseEnrollment.caseId) {
      tmpErrors.caseId = 'Borgers sag skal vælges';
      isValid = false;
    }

    if (courseId) {
      if (!followDfdgActivityPeriod && !beginAt) {
        tmpErrors.beginAt = 'Startdato skal angives';
        isValid = false;
      }

      if (!followDfdgActivityPeriod && !endAt) {
        tmpErrors.endAt = 'Slutdato skal angives';
        isValid = false;
      }

      if (desiredReportType === undefined || desiredReportType === null) {
        tmpErrors.desiredReportType = 'Frekvens for rapport skal vælges';
        isValid = false;
      }

      if (activityId) {
        const activity = activities.find(x => x.activityId === activityId);

        if (offerId && activity && activity.offerId !== offerId) {
          tmpErrors.activityNotOnOffer = 'Aktiviteten hører ikke til holdets tilbud. Vælg en anden aktivitet.';
          isValid = false;
        }

        if (followDfdgActivityPeriod && !activity?.endDate) {
          tmpErrors.activityInvalidEndDate = 'Aktiviteten har ingen slutdato. Vælg en anden aktivitet, eller fravælg "Følg aktivitetens periode"';
          isValid = false;
        } else {
          tmpErrors.activityInvalidEndDate = '';
        }
      }
    }

    setErrors({ ...tmpErrors });

    return isValid;
  }

  function applyCourse(course?: CourseDto) {
    setCourseEnrollment(draft => {
      draft.courseId = course?.courseId;
      draft.courseName = course?.name;
      if (course) {
        draft.createFollowUpAdvisOnMissingAttendance = course.createFollowUpAdvisOnMissingAttendance;
        draft.createNegativeEventOnMissingAttendance = course.createNegativeEventOnMissingAttendance;
        draft.createAdvisIfNotOnTime = course.createAdvisIfNotOnTime;
        draft.desiredReportType = course.desiredReportType;
        draft.hasDutyToWork = course.hasDutyToWork;
      }
    });

    // The course was looked up by the activity's offer, so only an unresolved course could clear offerId here.
    if (!activityIdQuery) {
      setOfferId(course?.offerId);
    }
    setErrors({ ...errors, courseId: '' });
  }

  async function handleCourseChange(course?: CourseDto) {
    applyCourse(course);
    try {
      // The chosen activity stays listed even if it is not on the new hold's tilbud; validation reports it on save.
      await loadContext(course?.offerId, courseEnrollment.activityId);
    } catch (error) {
      console.error(error);
    }
  }

  function handleBooleanChange<K extends keyof CourseEnrollment>(name: K, value?: boolean) {
    setCourseEnrollment(draft => {
      (draft as any)[name] = value;
    });
  }

  function handleDateChange<K extends keyof CourseEnrollment>(name: K, value?: DatePickerValue) {
    setCourseEnrollment(draft => {
      (draft as any)[name] = value;
    });
    setErrors({ ...errors, [name]: '' });
  }

  function handleReportTypeChange(value: ReportType) {
    setCourseEnrollment(draft => {
      draft.desiredReportType = value;
    });
    setErrors({ ...errors, desiredReportType: '' });
  }
  function handleCaseChange(value: string | undefined) {
    setCourseEnrollment(draft => {
      draft.caseId = value;
    });
    setErrors({ ...errors, caseId: '' });
  }

  function handleActivityChange(activity?: CourseEnrollmentActivityDto) {
    setCourseEnrollment(draft => {
      draft.activityId = activity?.activityId;
      draft.caseId = activity?.caseId;
      if (activity) {
        draft.followDfdgActivityPeriod = true;
        draft.beginAt = activity.startDate;
        draft.endAt = activity.endDate;
      } else {
        draft.followDfdgActivityPeriod = false;
      }
    });
  }

  return (
    <WithCitizenContext citizenId={citizenId}>
      <CourseEnrollmentComponent
        isEditmode={!!courseEnrollmentId}
        isLoading={isLoading}
        onSaveCourseEnrollmentClick={handleSaveCourseEnrollmentClick}
        onValidate={handleValidate}
        onCourseChange={handleCourseChange}
        courseEnrollment={courseEnrollment}
        onBooleanChange={handleBooleanChange}
        onCancelCourseEnrollment={handleCancelCourseEnrollmentClick}
        onDateChange={handleDateChange}
        onReportTypeChange={handleReportTypeChange}
        errors={errors}
        activities={activities}
        onActivityChange={handleActivityChange}
        onCaseChange={handleCaseChange}
        citizenId={citizenId}
        activityId={activityIdQuery}
        offerId={offerId}
        isInActivityCreateMode={!!activityIdQuery}
      />
      {isCancelDialogOpen && (
        <CancelCourseEnrollmentDialog
          isLoading={isLoading}
          hasStartedLessons={hasStartedLessons}
          hasFutureLessons={hasFutureLessons}
          onConfirm={handleConfirmCancelCourseEnrollment}
          onClose={() => setIsCancelDialogOpen(false)}
        />
      )}
      {isExistingLessonsDialogOpen && <ExistingLessonsDialog onClose={() => setIsExistingLessonsDialogOpen(false)} />}
      {lessonsWithoutFreeSeats.length > 0 && (
        <CourseCapacityWarningDialog
          lessons={lessonsWithoutFreeSeats}
          isLoading={isLoading}
          onConfirmClick={handleConfirmSave}
          onCancelClick={() => setLessonsWithoutFreeSeats([])}
        />
      )}
      {periodWithoutLessons && (
        <NoLessonsInPeriodDialog
          beginAt={periodWithoutLessons.beginAt}
          endAt={periodWithoutLessons.endAt}
          isLoading={isLoading}
          onConfirmClick={handleConfirmSave}
          onCancelClick={() => setPeriodWithoutLessons(undefined)}
        />
      )}
    </WithCitizenContext>
  );
}

export interface CourseEnrollment
  extends Omit<
    CourseEnrollmentDto,
    | 'courseId'
    | 'courseEnrollmentId'
    | 'beginAt'
    | 'endAt'
    | 'followDfdgActivityPeriod'
    | 'createNegativeEventOnMissingAttendance'
    | 'createFollowUpAdvisOnMissingAttendance'
    | 'createAdvisIfNotOnTime'
    | 'hasDutyToWork'
    | 'caseId'
  > {
  courseId?: string;
  caseId?: string;
  beginAt?: Date | string;
  endAt?: Date | string;
  courseName?: string;
  followDfdgActivityPeriod?: boolean;
  createNegativeEventOnMissingAttendance?: boolean;
  createFollowUpAdvisOnMissingAttendance?: boolean;
  createAdvisIfNotOnTime?: boolean;
  hasDutyToWork?: boolean;
}

export interface CourseEnrollmentErrors {
  courseId?: string;
  caseId?: string;
  beginAt?: string;
  endAt?: string;
  desiredReportType?: string;
  activityInvalidEndDate?: string;
  activityNotOnOffer?: string;
}
